import { Button, Dialog, DialogContent, DialogTitle } from "@mui/material";
import React from "react";
import DateSelect from "../../core/DateSelect";
import InputGroup from "../../core/InputGroup";
import CloseIcon from '@mui/icons-material/Close';
import styles from "./OfficeWorkDialog.module.scss";
import { formatNumber, str2number } from "../../../libs/helpers";
import { OfficeWorkOption } from "../../../apis/models/OfficeWorkResponse";
import { BrandlistResponseData } from "../../../apis/models/BrandlistResponse";
import { InvoiceListResponseData, InvoiceMasterData } from "../../../apis/models/InvoiceMasterRequest";
import { format } from "date-fns";
import DateWithoutTime from "../../../libs/dateWithoutTime";

type FeeType = {
  officeWorkFeeOptionId: string;
  quantity: string;
}

export type OfficeWorkDialogData = {
  dateAdded: DateWithoutTime;
  remarks: string;
  officeWorkFees: FeeType[];
}

type ErrorInfo = {
  dateAdded?: string;
  total?: string;
  officeWorkFees: FeeType[];
}

type RowType = OfficeWorkDialogData & {
  error: ErrorInfo
}

const createInitialItem = () => {
  return {
    dateAdded: DateWithoutTime.now(),
    remarks: "",
    officeWorkFees: [],
    error: {
      officeWorkFees: []
    }
  }
}

const OfficeWorkDialog = (props: OfficeWorkDialogProps) => {
  const {targetBrand, targetInvoice, targetInvoices, open, onClose, onSave, officeWorkCategoryOptions, existDates} = props;
  const [dialogData, setDialogData] = React.useState<RowType[]>([createInitialItem()]);

  const handleDateChange = (index: number, d: Date | null) => {
    const newItems = [...dialogData];
    const rowItem = newItems[index];
    rowItem.dateAdded = new DateWithoutTime(d)
    if (Object.keys(rowItem.error).length > 1 || rowItem.error.officeWorkFees.length !== 0) {
      rowItem.error = validateRow(rowItem)
    }
    newItems.splice(index, 1, rowItem);
    setDialogData(newItems);
  }

  const handleRemarkChange = (index: number, val: string) => {
    const newItems = [...dialogData];
    const rowItem = newItems[index];
    rowItem.remarks = val;
    if (Object.keys(rowItem.error).length > 1 || rowItem.error.officeWorkFees.length !== 0) {
      rowItem.error = validateRow(rowItem)
    }
    newItems.splice(index, 1, rowItem);
    setDialogData(newItems);
  }

  const handleInputChange = (index: number, optionId: string, value: string) => {
    const newItems = [...dialogData];
    const rowItem = newItems[index];
    const feeType = rowItem.officeWorkFees.find(fee => fee.officeWorkFeeOptionId === optionId)
    if (!feeType) {
      rowItem.officeWorkFees.push({
        officeWorkFeeOptionId: optionId,
        quantity: value,
      });
    } else {
      feeType.quantity = value;
    }
    if (Object.keys(rowItem.error).length > 1 || rowItem.error.officeWorkFees.length !== 0) {
      rowItem.error = validateRow(rowItem)
    }
    newItems.splice(index, 1, rowItem);
    setDialogData(newItems);
  }

  const getDateError = (rowData: RowType) => {
    if (rowData.error.dateAdded) {
      return rowData.error.dateAdded
    }
    if (duplicateDate.includes(rowData.dateAdded)) {
      return "日付が重複しています"
    }
    return undefined
  }

  const calculateTotal = (rowData: RowType) => {
    return rowData.officeWorkFees.reduce((prev, fee) => prev + str2number(fee.quantity), 0)
  }

  const deleteAddingItem = (index: number) => {
    if (dialogData.length <= 1) {
      return
    }
      const copyDialogData = [...dialogData];
      copyDialogData.splice(index, 1);
      setDialogData(copyDialogData);
  }

  const addEmptyRow = () => {
    setDialogData([...dialogData, createInitialItem()]);
  }

  const validateRow = (item: RowType) => {
    const errorFees = item.officeWorkFees
      .map(fee => {
        return {
          officeWorkFeeOptionId: fee.officeWorkFeeOptionId,
          quantity: validateNumber(fee.quantity),
        } as FeeType
      })
      .filter(fee => fee.quantity !== undefined)

    const error: ErrorInfo = {
      dateAdded: validateDateAdded(item.dateAdded),
      total: calculateTotal(item) <= 0 ? "個数を入力してください" : undefined,
      officeWorkFees: errorFees
    }
    return error
  }

  const validateDateAdded = (value: DateWithoutTime): string | undefined => {
    const dateString = value.getValue4Display()
    if (value.getValue4Display().length === 0) {
      return "入力必須です"
    } else if (value.isInvalid()) {
      return "年月日で入力してください"
    } else if (existDates.some(d => d.equals(value))) {
      return "すでに入力されている日付です"
    } else if (!targetInvoice && !getInvoiceAtDate(dateString)) {
      return "請求期間の範囲で入力してください"
    } else if (targetInvoice) {
      const invoiceFrom = format(new Date(targetInvoice.from), "yyyy/MM/dd")
      const invoiceTo = format(new Date(targetInvoice.to), "yyyy/MM/dd")
      if (dateString < invoiceFrom || invoiceTo < dateString) {
        return "請求期間の範囲で入力してください"
      }
    }
    return undefined
  }
  
  const validateNumber = (value: string) => {
    if (value.length === 0) {
      return undefined
    } else if (isNaN(+value) || parseInt(value) < 1) {
      return "１以上を入力してください"
    }
    return undefined
  }

  const getInvoiceAtDate = (value: string) => {
    if (!targetInvoices || targetInvoices.length <= 0) {
      return undefined
    }
    return targetInvoices.find(invoice => {
      const invoiceFrom = format(new Date(invoice.from), "yyyy/MM/dd")
      const invoiceTo = format(new Date(invoice.to), "yyyy/MM/dd")
      return (invoiceFrom <= value  && value <= invoiceTo)
    })
  }

  const isDisabledSave = () => {
    if (dialogData.length <= 0 || duplicateDate.length > 0) {
      return true
    }
    return dialogData.some((item, index) => {
      const err = validateRow(item)
      item.error = err
      const newItems = [...dialogData]
      newItems.splice(index, 1, item)
      setDialogData(newItems)
      return err.dateAdded || err.total || err.officeWorkFees.some(fee => fee.quantity)
    })
  }

  const saveAddedItem = () => {
    if (isDisabledSave()) {
      return;
    }
    dialogData.forEach(item => {
      item.officeWorkFees = item.officeWorkFees.filter(size => str2number(size.quantity) > 0);
    })
    onSave(dialogData);
  }

  const closeDialog = () => {
    onClose();
  }

  const getCellValue = (rowIndex: number, optionId: string) => {
    const rowData = dialogData[rowIndex];
    const fee = rowData.officeWorkFees.find(fee => fee.officeWorkFeeOptionId === optionId);
    return fee ? fee.quantity : ""
  }

  const invoicePeriod = React.useMemo(() => {
    if (targetInvoices && targetInvoices.length > 0) {
      const min = targetInvoices.reduce((prev, i) => prev < i.from ? prev : i.from , "3000-12-31")
      const max = targetInvoices.reduce((prev, i) => prev > i.to ? prev : i.to , "")
      return `${format(new Date(min), "yyyy/MM/dd")} ～ ${format(new Date(max), "yyyy/MM/dd")}`
    } else if (targetInvoice){
      return `${format(new Date(targetInvoice.from), "yyyy/MM/dd")} ～ ${format(new Date(targetInvoice.to), "yyyy/MM/dd")}`
    }
    return ''
  }, [targetInvoices, targetInvoice])

  const optionMaster = React.useMemo(() => {
    return officeWorkCategoryOptions
      .filter(o => o.officeWorkFeeOption.unitPrice > 0 || !o.officeWorkFeeOptionId) // remove category
      .sort((a,b) => (a.order ?? 0) - (b.order ?? 0))
      .map(o => {
        return {
          officeWorkFeeOptionId: o.officeWorkFeeOptionId!,
          workDetails: o.officeWorkFeeOption.workDetails,
          unitPrice: o.officeWorkFeeOption.unitPrice,
        }
      });
  }, [officeWorkCategoryOptions]);

  const optionCategory = React.useMemo(() => {
    return officeWorkCategoryOptions
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .reduce((group: {category: string, count: number}[], option: OfficeWorkOption) => {
        if (option.officeWorkFeeOption.unitPrice <= 0) {
          return group
        }
        const key = option.officeWorkFeeOption.category
        const officeWork = group.find(e => e.category === key)
        if (!officeWork) {
          group.push({ category: key, count: 1 })
        } else {
          officeWork.count = officeWork.count + 1
        }
        return group
      }, []);
  }, [officeWorkCategoryOptions]);

  const duplicateDate = React.useMemo(() => {
    const dateList = new Set<DateWithoutTime>()
    dialogData.forEach(data => {
      if (dialogData.filter(d => d.dateAdded.equals(data.dateAdded)).length > 1) {
        dateList.add(data.dateAdded)
      }
    })
    return Array.from(dateList)
  }, [dialogData])

  return (
      <Dialog open={open} onClose={() => {closeDialog()}} fullWidth={true} maxWidth="lg" PaperProps={{sx: {height: '90%'}}}>
          <DialogTitle sx={{fontWeight: 700, fontSize: "24px", paddingLeft: "40px"}}>{targetBrand?.brandName ?? ''} - 事務費用追加</DialogTitle>
          <DialogContent sx={{padding: "0 40px 24px", display: "flex", flexDirection: "column"}}>
              <div className={styles.dialog__label}>
                <span>請求期間：</span>
                <span className={styles.dialog__label_content}>{invoicePeriod}</span>
              </div>
              <div className={styles.dialog__content}>
                  {
                      dialogData.map((dialogItem, objIndex) => (
                          <div className={styles.dialog__content_item} key={objIndex}>
                              <div className={styles.dialog__content_item_total}>
                                <DateSelect
                                  selectedDate={dialogItem.dateAdded.getDate()}
                                  handleDateChange={(date) => handleDateChange(objIndex, date)}
                                  label="日付"
                                  required
                                  errMess={getDateError(dialogItem)}
                                />
                                <InputGroup
                                  label="合計"
                                  type="text"
                                  disabled
                                  value={calculateTotal(dialogItem)}
                                  error={!!(dialogItem.error.total)}
                                  errorMessage={dialogItem.error.total}
                                />
                                <CloseIcon className={styles.dialog__content_item_icon} onClick={() => deleteAddingItem(objIndex)}/>
                              </div>
                              <div className={styles.table_wrapper}>
                                <table>
                                  <tbody>
                                    <tr className={styles.category}>
                                      {optionCategory.map((category, index) => (
                                        <th key={index} colSpan={category.count}>{category.category}</th>
                                      ))}
                                    </tr>
                                    <tr className={styles.works}>
                                      {optionMaster.map(option => (
                                        <td key={option.officeWorkFeeOptionId}>
                                          {option.workDetails}<br/>({formatNumber(option.unitPrice)})
                                        </td>
                                      ))}
                                    </tr>
                                    <tr className={styles.input}>
                                      {optionMaster.map(option => (
                                        <td key={option.officeWorkFeeOptionId}>
                                          <InputGroup
                                            type="number"
                                            value={getCellValue(objIndex, option.officeWorkFeeOptionId)}
                                            onChange={(val) => handleInputChange(objIndex, option.officeWorkFeeOptionId, val)}
                                            error={dialogItem.error.officeWorkFees.some(f => f.officeWorkFeeOptionId === option.officeWorkFeeOptionId)}
                                            errorMessage={dialogItem.error.officeWorkFees.find(f => f.officeWorkFeeOptionId === option.officeWorkFeeOptionId)?.quantity}
                                          />
                                        </td>
                                      ))}
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                              <div className={styles.remarks}>
                                  <InputGroup type="text" label="備考" value={dialogItem.remarks} onChange={(val) => handleRemarkChange(objIndex, val)}/>
                              </div>
                          </div>
                      ))
                  }
              </div>
              <div className={styles.dialog__add}>
                  <Button variant="outlined" color="primary" onClick={() => addEmptyRow()}>＋行追加</Button>
              </div>
              <div className={styles.dialog__functions}>
                  <Button variant="outlined" color="primary" onClick={() => {closeDialog()}}>キャンセル</Button>
                  <Button variant="contained" color="primary" onClick={() => saveAddedItem()}>登録</Button>
              </div>
          </DialogContent>
      </Dialog>
  )
}

export default OfficeWorkDialog;

export type OfficeWorkDialogProps = {
    targetBrand?: BrandlistResponseData,
    targetInvoice?: InvoiceMasterData,
    targetInvoices?: InvoiceListResponseData[],
    open: boolean,
    onClose: () => void,
    onSave: (items: OfficeWorkDialogData[]) => void,
    officeWorkCategoryOptions: OfficeWorkOption[],
    existDates: DateWithoutTime[],
}